import localforage from "localforage";
import { defineStore } from "pinia";
import sortBy from "sort-by";
import type { Ref } from "vue";
import { ref, watch } from "vue";
import { ApiAdapter } from "./adapter";

export function getClientId() {
  if (!localStorage.clientid) {
    localStorage.clientid = "_" + Math.random().toString(36).slice(2, 20);
  }
  return localStorage.clientid;
}

export interface CreateStoreOptions {
  endpoint?: string;
  local?: boolean;
  syncTimeout?: number;
  responseKey?: string;
  responseErrorKey?: string;
  sortKey?: string;
  version?: string;
  namespace?: string;
}

export function watchStorageKey(key, callback: (value: any) => void) {
  window.addEventListener("storage", async function (e) {
    if (e.key === key) {
      callback(localStorage[e.key]);
    }
  });
}

export function watchStorageTimestamp(key, callback: (value: Date) => void) {
  let current = new Date(localStorage[key] || 0);
  if (current > new Date()) {
    current = new Date();
  }
  watchStorageKey(key, (value) => {
    let dt = new Date(value);
    if (dt > new Date()) {
      dt = new Date();
    }
    if (current < dt) {
      current = dt;
      callback(dt);
    }
  });
}

export function createStore<T extends { id: number }>(
  name: string,
  options: CreateStoreOptions = {},
) {
  const opts: CreateStoreOptions = {
    endpoint: options.endpoint || name,
    local: options.local || true,
  };
  return defineStore(name, () => {
    const adapter = new ApiAdapter<T>(opts.endpoint, {
      responseKey: options.responseKey,
    });
    let storageName = name;
    if (options.version) {
      storageName += `:${options.version}`;
    }
    const store: LocalForage | null = opts.local
      ? localforage.createInstance({
          storageName: options.namespace,
          name: storageName,
        })
      : null;
    const lastUpdateKey = `${name}:lastUpdate`;
    const updated = ref<Date>(new Date(localStorage[lastUpdateKey] || 0));
    const list: Ref<T[]> = ref([]);
    const clientId = ref<string>(getClientId());

    watch(updated, () => {
      localStorage[lastUpdateKey] = updated.value.toISOString();
    });

    if (store) {
      store.iterate((row) => {
        list.value.push(row as T);
      });
      if (options.sortKey) {
        list.value.sort(sortBy(options.sortKey));
      }
      watchStorageKey("clientid", (value) => (clientId.value = value));
      watchStorageTimestamp(lastUpdateKey, (dt) => {
        updateFromStorage(dt);
        updated.value = dt;
      });
    }

    function log(...args) {
      console.log(
        `%c${name}:store`,
        "color: darkred;background: white; padding: 1px 3px; border-radius: 3px;",
        ...args,
      );
    }

    async function updateFromStorage(dt?: Date) {
      if (store) {
        log("update from storage", dt ? dt.toISOString() : "");
        const newList: T[] = [];
        await store.iterate((row) => {
          newList.push(row as T);
        });
        if (options.sortKey) {
          newList.sort(sortBy(options.sortKey));
        }
        list.value = newList;
      }
    }

    async function maybeSync(
      opts: { allPages: boolean } = { allPages: true },
    ): Promise<Ref<T[]>> {
      if (!options.syncTimeout) {
        throw new Error(
          "No `syncTimeout` set for this store, cannot use `maybeSync`",
        );
      }
      if (updated.value < new Date(Date.now() - options.syncTimeout)) {
        await sync(opts);
        updated.value = new Date();
      }
      return list;
    }

    async function sync(
      opts: { allPages: boolean } = { allPages: true },
    ): Promise<Ref<T[]>> {
      try {
        let response = await adapter.list({}, opts.allPages);
        if (store) {
          const existing = list.value.map((v) => v.id);

          for (const row of response) {
            await store.setItem(`${row.id}`, row);
            existing.splice(existing.indexOf(row.id), 1);
          }

          // remove items that weren't updated
          for (const id of existing) {
            await store.removeItem(`${id}`);
          }
        }
        list.value = response;
        if (options.sortKey) {
          list.value.sort(sortBy(options.sortKey));
        }
      } catch (ex) {
        log("sync failed", ex);
      }
      return list;
    }

    async function save(
      record: T,
    ): Promise<{ error?: any; errorDetail?: any; id?: number; instance?: T }> {
      const body = await adapter.update(record.id, record);

      // check for errors
      let error: any = null;
      for (const key of [options.responseErrorKey, "errors", "error"]) {
        if (key && body[key]) {
          error = body[key];
          return { error };
        }
      }

      if (!record.id) {
        if (!body.id) {
          return { error: "Response is missing ID", errorDetail: body };
        }
        record.id = body.id;
        list.value.push(record);
        if (options.sortKey) {
          list.value.sort(sortBy(options.sortKey));
        }
      } else {
        const row = list.value.find((row) => row.id == record.id);
        for (const prop in record) {
          row[prop] = record[prop];
        }
        if (store) {
          await store.setItem(`${record.id}`, record);
          updated.value = new Date();
        }
        record = row;
      }
      return { id: record.id, instance: record };
    }

    async function remove(id: number): Promise<void> {
      await adapter.delete(id);
      if (store) {
        await store.removeItem(`${id}`);
        updated.value = new Date();
      }
    }

    return {
      adapter,
      updated,
      store,
      list,
      sync,
      maybeSync,
      save,
      remove,
    };
  });
}
